import React, {ReactNode, useEffect, useRef, useState} from 'react';
import './Help.less';
import IconButton from "../IconButton/IconButton";
import Icons from "../IconButton/Icons";
import ReactDOM from 'react-dom';
import ls from '../../lib/localStorage';
import {isMobileView} from "../../lib/picos/browserFns";

export type HelpProps = {
    // Should the icon pulsate for extra attention?
    beat?: boolean,

    // Title as a string or element
    title: ReactNode,

    // Title to use for ID if title is an element
    titleAsString?: string,

    // info or error
    type?: 'error' | 'info', // info, error
    message?: ReactNode,
    fixedPosition?: boolean, // Should we use [position: fixed] instead of [position: absolute]?

    // Always show this help icon, even if Help is turned off
    showOverride?: boolean,

    justifyContent?: 'flex-end' | 'flex-start',
    horizontalCenter?: 'left' | 'center' | 'right',

    invertColors?: boolean, // Invert the colors of the help icon and message

    // Should we center this vertically?
    verticalCenter?: boolean
}

type HelpState = {
    isOpen: boolean,
    appContentOverflow: string,
    pageX: number,
    pageY: number,
    pageHeight: number,
    pageWidth: number,
    justifyContent: 'flex-end' | 'flex-start',
}

const Help: React.FC<HelpProps> = (props) => {
    const [state, setState] = useState<HelpState>(() => ({
        isOpen: false,
        appContentOverflow: '',
        pageX: 0,
        pageY: 0,
        pageHeight: 0,
        pageWidth: 0,
        justifyContent: props.justifyContent || 'flex-end',
    }));

    const setPartialState = (partial: Partial<HelpState>) =>
        setState(prev => ({...prev, ...partial}));

    const saveRef = useRef<HTMLDivElement>(null);
    const stateRef = useRef(state);
    stateRef.current = state;

    const onClickOff = (event: any) => {
        const cls = event.target?.className;
        if (typeof (cls) === 'string' && cls.includes('icon-button')) return;

        // If we clicked outside this element, close the menu
        if (saveRef.current && event.target !== saveRef.current && !saveRef.current.contains(event.target).valueOf()) {
            setPartialState({isOpen: false});
        }
    };

    /**
     * Get the current overflowY state for AppContent on mount; restore on unmount.
     */
    useEffect(() => {
        if (window.__is_mobile__) document.addEventListener('touchstart', onClickOff);
        else document.addEventListener('mousedown', onClickOff);

        const appContent = document.getElementById('AppContent');
        if (stateRef.current.appContentOverflow !== appContent.style.overflowY)
            setPartialState({appContentOverflow: appContent.style.overflowY});

        return () => {
            if (window.__is_mobile__) document.removeEventListener('touchstart', onClickOff);
            else document.removeEventListener('mousedown', onClickOff);

            const ac = document.getElementById('AppContent');
            if (ac) ac.style.overflowY = stateRef.current.appContentOverflow;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Toggle between open and close states.
     *
     * We want to set the AppContent's overflow scrolling off when we are showing the Help message.
     */
    const toggleOpen = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const appContent = document.getElementById('AppContent');
        appContent.style.overflowY = state.isOpen ? 'auto' : 'hidden';

        setPartialState({
            isOpen: !state.isOpen,
            pageX: e.pageX,
            pageY: e.pageY + 16,
            pageHeight: e.view.innerHeight,
            pageWidth: e.view.innerWidth
        });
    };

    const createPortal = (div: ReactNode) => {
        if (isMobileView()) return div;
        else return ReactDOM.createPortal(div, document.getElementById('portal-root'));
    };

    if (isMobileView()) return null;

    const type = props.type || 'info';

    if (!props.showOverride && type !== 'error') {
        const showHelp = ls.getBoolean(ls.SHOW_HELP, true);
        if (!showHelp) return null;
    }

    // Calculate position
    const MESSAGE_WIDTH = 30.5 * 16; // IMPORTANT: Update this if the width of the box in CSS is changed.
    const {pageX, pageY, pageWidth, pageHeight} = state;
    const bottom = pageHeight - pageY - 32;
    const left = (MESSAGE_WIDTH + pageX) > pageWidth ? pageWidth - MESSAGE_WIDTH : pageX;

    const button = type === 'info' ? Icons.HELP : Icons.ERROR;

    const id = props.titleAsString || '';

    let primaryColor = 'white';
    let secondaryColor = state.isOpen ? 'maroon' : type === 'info' ? 'var(--sl-color-green-700)' : 'red';

    return (
        <div ref={saveRef}
             className={`help ${props.verticalCenter ? 'help--vertical' : ''}`}
             style={{justifyContent: 'left'}}
        >
            <IconButton id={`help__icon__${id}`}
                        className='help__icon'
                        type={state.isOpen ? Icons.SEARCH_CANCEL_DUO : button}
                        onClick={toggleOpen}
                        primaryColor={props.invertColors ? secondaryColor : primaryColor}
                        secondaryColor={props.invertColors ? primaryColor : secondaryColor}
                        title={props.title}
                        tabIndex={-1}
                        clickPassthrough={false}
                        beat={props.beat}
            />
            {state.isOpen && createPortal(
                <div
                    className={`help__message-border${props.fixedPosition ? ' help--fixed-position' : ''}`}
                    style={isMobileView() ? null : {top: pageY, left}}
                >
                    <div className='help__message'>
                        <div className="help__header">
                            {isMobileView() && <IconButton id='cancel-help'
								                               type={Icons.SEARCH_CANCEL}
								                               onClick={toggleOpen}/>
                            }
                        </div>
                        <div className='help__message-block'
                             style={{maxHeight: bottom}}>{props.message}</div>
                    </div>
                </div>)
            }
        </div>
    );
};

export default Help;
